import React, { useEffect, useState } from "react";
import "./Template.css";

import TemplateFilter from "./TemplateFilter";
import TemplateTable from "./TemplateTable";
import TemplatePagination from "./TemplatePagination";
import { publicApiClient } from "../../../../api/apiClient";

const formatDate = (value) => {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 10).replaceAll("-", ".");
  }

  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");

  return `${yyyy}.${mm}.${dd}`;
};

const getListFromResponse = (result) => {
  const data = result?.data || result;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.list)) return data.list;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.data?.content)) return data.data.content;

  return [];
};

const normalizeNotice = (item) => {
  const rawDate =
    item.date || item.createdAt || item.createdDate || item.updatedAt;

  return {
    id:
      item.id ||
      item.noticeId ||
      item.activityNoticeId ||
      item.activityId ||
      item.postId ||
      item.boardId,
    title: item.title || "",
    writer:
      item.writer ||
      item.userName ||
      item.createdBy ||
      item.author ||
      "총학생회",
    date: formatDate(rawDate),
    rawDate,
    view: item.viewer ?? item.view ?? item.views ?? item.viewCount ?? 0,
    content: item.content || item.contents || item.description || "",
  };
};

const sortLatestFirst = (a, b) => {
  const dateA = new Date(a.rawDate).getTime();
  const dateB = new Date(b.rawDate).getTime();

  if (!Number.isNaN(dateA) && !Number.isNaN(dateB)) {
    return dateB - dateA;
  }

  return Number(b.id || 0) - Number(a.id || 0);
};

const getNoticeDetailContent = async (id) => {
  if (!id) return "";

  try {
    const result = await publicApiClient(`/api/activity/notice/${id}`, {
      method: "GET",
    });

    const data = result?.data || result;

    return (
      data?.content ||
      data?.contents ||
      data?.description ||
      data?.body ||
      ""
    );
  } catch (error) {
    console.error("공지사항 상세 내용 조회 실패:", error);
    return "";
  }
};

export default function Template() {
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("title");
  const [page, setPage] = useState(1);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);

  const loadMissingContents = async () => {
    const hasMissingContent = data.some((item) => item.id && !item.content);

    if (!hasMissingContent) return;

    try {
      setContentLoading(true);

      const nextData = await Promise.all(
        data.map(async (item) => {
          if (!item.id || item.content) return item;

          const detailContent = await getNoticeDetailContent(item.id);

          return {
            ...item,
            content: detailContent,
          };
        })
      );

      setData(nextData);
    } finally {
      setContentLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchType === "content") {
      await loadMissingContents();
    }

    setSearchQuery(search);
    setPage(1);
  };

  useEffect(() => {
    const fetchNoticeList = async () => {
      try {
        setLoading(true);

        const result = await publicApiClient(
          "/api/activity/notice?searchCategory=TITLE&page=0&size=100",
          {
            method: "GET",
          }
        );

        const list = getListFromResponse(result)
          .map(normalizeNotice)
          .filter((item) => item.id)
          .sort(sortLatestFirst);

        setData(list);
      } catch (error) {
        console.error("공지사항 목록 조회 실패:", error);
        setData([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNoticeList();
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchType === "content") {
        await loadMissingContents();
      }

      setSearchQuery(search);
      setPage(1);
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchType]);

  const filteredData = data.filter((item) => {
    const keyword = searchQuery.trim();

    if (!keyword) return true;

    if (searchType === "content") {
      return (item.content || "").includes(keyword);
    }

    return (item.title || "").includes(keyword);
  });

  const itemsPerPage = 10;
  const total = filteredData.length;
  const totalPages = Math.ceil(total / itemsPerPage);
  const safePage = total === 0 ? 1 : Math.min(page, totalPages || 1);

  useEffect(() => {
    if (total > 0 && page > totalPages) {
      setPage(1);
    }
  }, [total, page, totalPages]);

  const pagedData = filteredData.slice(
    (safePage - 1) * itemsPerPage,
    safePage * itemsPerPage
  );

  return (
    <div className="notice-template">
      <div className="notice-template-inner">
        <div className="notice-template-header">
          총학생회에서 주요 소식과 공지사항을 안내드립니다.
        </div>

        <TemplateFilter
          search={search}
          setSearch={setSearch}
          searchType={searchType}
          setSearchType={setSearchType}
          handleSearch={handleSearch}
        />

        <div className="notice-template-layout">
          <div className="notice-template-content">
            {loading || contentLoading ? (
              <div className="notice-template-empty">불러오는 중입니다.</div>
            ) : (
              <TemplateTable
                data={pagedData}
                page={safePage}
                itemsPerPage={itemsPerPage}
                total={total}
              />
            )}
          </div>

          {total > 0 && (
            <TemplatePagination
              page={safePage}
              setPage={setPage}
              total={total}
              itemsPerPage={itemsPerPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}